import { useQuery } from "@tanstack/react-query";
import { PageContainer } from "@kkp/ui";
import { Card } from "@kkp/ui";
import { formatCurrency, formatNumber } from "@kkp/app-kit";
import { listRecipients } from "../../api/recipients";
import { listBulkTransfers } from "../../api/transactions";
import { getProfile } from "../../api/profile";
import { getCompanyDetail } from "../../api/companies";
import { summarizeDashboard } from "./summary";

export default function DashboardPage() {
  const { data: recipientsData } = useQuery({
    queryKey: ["recipients"],
    queryFn: listRecipients,
    staleTime: 0,
  });
  const { data: transactionsData } = useQuery({
    queryKey: ["bulk-transfers"],
    queryFn: listBulkTransfers,
    staleTime: 0,
  });
  const { data: profileData } = useQuery({
    queryKey: ["client-profile"],
    queryFn: getProfile,
    staleTime: 0,
  });
  const companyId = profileData?.companyId ?? "";
  const { data: companyDetail } = useQuery({
    queryKey: ["company-detail", companyId],
    queryFn: () => getCompanyDetail(companyId),
    enabled: Boolean(companyId),
    staleTime: 0,
  });

  const recipients = recipientsData ?? [];
  const transactions = transactionsData ?? [];

  const activeRecipients = recipients.filter(
    (recipient) => recipient.status === "Active",
  ).length;

  // Counts only — no client-side money arithmetic on the dashboard.
  const summary = summarizeDashboard(transactions);

  const stats = [
    {
      label: "Current pending bulk transaction count",
      value: formatNumber(summary.pendingCount),
    },
    {
      label: "Total executed bulk transactions count",
      value: formatNumber(summary.executedCount),
    },
    {
      label: "Total success transaction count",
      value: formatNumber(summary.successRecipientCount),
    },
    {
      // Authoritative settled aggregate from the server (fee-free, a MoneyIdr
      // string). NOT a client-side reduce of per-bulk receivable, and never
      // includes the platform fee.
      label: "Total success transaction volume",
      value: formatCurrency(companyDetail?.totalTransactionsAmountIdr),
    },
    {
      label: "Total failed transaction count",
      value: formatNumber(summary.failedCount),
    },
    {
      label: "Total active recipients",
      value: formatNumber(activeRecipients),
    },
  ];

  return (
    <PageContainer
      title="Dashboard"
      description="Summary of bulk transaction activity."
      width="wide"
    >
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {stats.map((stat) => (
          <Card key={stat.label} className="flex flex-col gap-2">
            <div className="text-xs text-[color:var(--kkp-text-muted)]">
              {stat.label}
            </div>
            <div className="text-xl font-semibold text-white">{stat.value}</div>
          </Card>
        ))}
      </div>
    </PageContainer>
  );
}
